import { useEffect, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Snackbar, Alert } from '@mui/material';

import TripPreferencesDialog from './TripPreferencesDialog';
import LabelValue from '../../../LabelValue';
import * as providerSlice from '../../../../data/provider.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { TripPreferences } from '../../../../data/models/trip-preferences.model';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import preferenceUtils from '../../../../services/preferenceUtils';
import { useCanEditProvider } from '../../../../hooks/permissions/useCanEditProvider';
import { DateTime } from 'luxon';

interface TripPreferencesComponentProps {
  providerId: number | null | undefined;
}

export default function TripPreferencesComponent({
  providerId,
}: TripPreferencesComponentProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>();
  const loading = useAppSelector(providerSlice.selectLoadingTripPreferences);
  const loadingError = useAppSelector(
    providerSlice.selectLoadingTripPreferencesError,
  );
  const tripPreferences = useAppSelector(providerSlice.selectTripPreferences);

  const isEditable = providerId && useCanEditProvider(providerId);

  const [tripHours, setTripHours] = useState<
    { dayName: string; hours: string }[]
  >([]);
  const [vehicleTripModes, setVehicleTripModes] = useState<string[]>([]);

  useEffect(() => {
    if (providerId) {
      dispatch(providerSlice.fetchProviderTripPreferencesAsync(providerId));
    }
  }, [providerId]);

  useEffect(() => {
    setSnackbarMessage(loadingError || undefined);
  }, [loadingError]);

  const onSnackbarClose = () => {
    setSnackbarMessage(undefined);
    dispatch(providerSlice.setLoadingTripPreferencesError(null));
  };

  useEffect(() => {
    const hours: { dayName: string; hours: string }[] = [];

    tripPreferences?.hoursOfOperations
      .filter((v) => v.hoursOfOperationState === 'active')
      .sort((a, b) => a.frequencyDayOfWeekID - b.frequencyDayOfWeekID)
      .forEach((day) => {
        const dayShortName =
          day.frequencyDayOfWeek?.frequencyDayOfWeekName?.substring(
            0,
            3,
          ) as string;
        const startTime = DateTime.fromFormat(
          day.startServiceOn!,
          'HH:mm:ss',
        ).toFormat('hh:mma');
        const endTime = DateTime.fromFormat(
          day.endServiceOn!,
          'HH:mm:ss',
        ).toFormat('hh:mma');

        hours.push({
          dayName: dayShortName,
          hours: `${startTime} - ${endTime}`,
        });
      });
    setTripHours(hours);

    const total =
      tripPreferences?.vehicleTripModeCapacities.reduce(
        (partialSum, a) => partialSum + a.capacity,
        0,
      ) || 0;

    const vals: string[] = [];
    if (total > 0) {
      vals.push(`${total} total`);
      tripPreferences?.vehicleTripModeCapacities.forEach((tripMode) => {
        if (tripMode.capacity > 0) {
          vals.push(`${tripMode.capacity} ${tripMode.tripMode?.tripModeName}`);
        }
      });
    }
    setVehicleTripModes(vals);
  }, [tripPreferences]);

  const formatVehicleTripModes = (prefs: TripPreferences): string[] => {
    const total = prefs.vehicleTripModeCapacities.reduce(
      (partialSum, a) => partialSum + a.capacity,
      0,
    );

    const vals: string[] = [];

    vals.push(`${total} total`);

    if (total > 0) {
      prefs.vehicleTripModeCapacities.forEach((tripMode) => {
        if (tripMode.capacity > 0) {
          vals.push(`${tripMode.capacity} ${tripMode.tripMode?.tripModeName}`);
        }
      });

      return vals;
    }
    return [];
  };

  const closeDialog = () => {
    setDialogOpen(false);
    dispatch(providerSlice.resetSavingFlags());
  };

  return (
    <>
      {loading ? (
        <Box justifyContent="center" display="flex" paddingY={4}>
          <CircularProgress sx={{ margin: 'auto' }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item lg={3} md={6} sm={12}>
            <LabelValue
              label={t('preferences.coverageAreas')}
              value={
                tripPreferences?.coverageAreas
                  ?.map((ca) => ca.coverageAreaName)
                  .join(', ') || ''
              }
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12}>
            <LabelValue label={t('preferences.hours')} value="" />
            {tripHours.map((h, idx) => (
              <Box key={idx} display="flex">
                <Box sx={{ width: 40 }}>
                  <LabelValue label="" value={h.dayName} />
                </Box>
                <LabelValue label="" value={h.hours} />
              </Box>
            ))}
          </Grid>
          <Grid item lg={3} md={6} sm={12}>
            <LabelValue label={t('preferences.vehicles')} value="" />
            {vehicleTripModes.map((value, idx) => (
              <LabelValue key={idx} label="" value={value} />
            ))}
          </Grid>
          <Grid item lg={3} md={6} sm={12}>
            <LabelValue
              label={t('preferences.tripsPerDay')}
              value={
                !!tripPreferences?.capacityOfTripsToServiceInDay
                  ? tripPreferences?.capacityOfTripsToServiceInDay.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12}>
            <LabelValue
              label={t('preferences.timeZone')}
              value={
                tripPreferences?.hoursOfOperations
                  ? preferenceUtils.getTimeZoneName(
                      tripPreferences.hoursOfOperations,
                    )
                  : ''
              }
            />
          </Grid>
        </Grid>
      )}

      {isEditable && !loading && (
        <Box mt={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDialogOpen(true)}>
            {t('common.edit')}
          </Button>
        </Box>
      )}

      <TripPreferencesDialog
        title={t('preferences.title')}
        open={dialogOpen}
        providerId={providerId}
        pref={tripPreferences}
        close={closeDialog}
      />

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={onSnackbarClose} variant="filled" severity="error">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
